import React, { useState, useEffect, CSSProperties } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  upsertWebhook, listenToWebhooks, cleanupWebhookListener,
  selectWebhooks, selectWebhookLoading, selectWebhookUpserting, selectWebhookError,
  clearError, WebhookEndpoint,
} from "../../../redux/webhookSlice";
import {
  selectMerchantId, selectMode,
} from "../../../redux/merchantSlice";
import { RootState, AppDispatch } from "../../../redux/store";

/* ─── PALETTE ─── */
const IVORY    = "#F5F0EB";
const CREAM    = "#EDE8E1";
const STONE    = "#D4CFC8";
const WARMGRAY = "#9B958D";
const DUSK     = "#6B6560";
const NIGHT    = "#1A1714";
const PURE_W   = "#FFFFFF";
const PURE_B   = "#000000";
const ROUGE    = "#C8303A";
const SAGE     = "#4A7C59";
const AMBER    = "#B8860B";
const PURPLE   = "#534AB7";
const PURPLE_L = "#EEEDFE";

const fontHref = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Libre+Franklin:wght@300;400;500;600;700&display=swap";
if (typeof document !== "undefined" && !document.head.querySelector(`link[href="${fontHref}"]`)) {
  const link = document.createElement("link"); link.rel = "stylesheet"; link.href = fontHref;
  document.head.appendChild(link);
}
const serif: CSSProperties = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const sans:  CSSProperties = { fontFamily: "'Libre Franklin', 'Helvetica Neue', sans-serif" };
const mono:  CSSProperties = { fontFamily: "'Courier New', monospace" };

const API = "https://europe-west3-cosmoola.cloudfunctions.net";

/* ─── EVENTS ─── */
const EVENT_GROUPS = [
  { group: "Payment Intent", events: [
    { id: "payment_intent.succeeded",       desc: "A payment intent has successfully completed." },
    { id: "payment_intent.payment_failed",  desc: "A payment intent has failed." },
    { id: "payment_intent.canceled",        desc: "A payment intent has been canceled." },
    { id: "payment_intent.created",         desc: "A new payment intent was created." },
    { id: "payment_intent.processing",      desc: "A payment intent has started processing." },
    { id: "payment_intent.requires_action", desc: "Needs additional action from the customer." },
  ]},
  { group: "Checkout", events: [
    { id: "checkout.session.completed",               desc: "A checkout session was successfully completed." },
    { id: "checkout.session.async_payment_succeeded", desc: "A delayed payment method finally succeeded." },
    { id: "checkout.session.async_payment_failed",    desc: "A delayed payment method has failed." },
    { id: "checkout.session.expired",                 desc: "A checkout session has expired." },
  ]},
  { group: "Charge", events: [
    { id: "charge.succeeded",      desc: "A charge has been successfully created." },
    { id: "charge.failed",         desc: "A charge attempt has failed." },
    { id: "charge.refunded",       desc: "A charge has been refunded." },
    { id: "charge.refund.updated", desc: "A refund has been updated." },
  ]},
  { group: "Payout", events: [
    { id: "payout.paid",    desc: "A payout has been paid out." },
    { id: "payout.failed",  desc: "A payout has failed." },
    { id: "payout.created", desc: "A payout has been created." },
  ]},
];

const DEFAULT_EVENTS = [
  "payment_intent.succeeded","payment_intent.payment_failed","payment_intent.canceled",
  "charge.succeeded","charge.refunded","checkout.session.completed","payout.paid","payout.failed",
];

type Step = 1 | 2 | 3 | 4;

/* ============================================================
   MAIN — no props, reads everything from Redux
   ============================================================ */
export default function Webhooks() {
  const dispatch   = useDispatch<AppDispatch>();
  const endpoints  = useSelector(selectWebhooks);
  const loading    = useSelector(selectWebhookLoading);
  const upserting  = useSelector(selectWebhookUpserting);
  const storeError = useSelector(selectWebhookError);
  const merchantId = useSelector(selectMerchantId) ?? "";
  const mode       = useSelector(selectMode);
  // secret key from persisted keys in merchantSlice
  const secretKey  = useSelector((s: RootState) =>
    s.merchant.mode === "live"
      ? s.merchant.keys?.secret_key_live ?? ""
      : s.merchant.keys?.secret_key_test ?? ""
  );

  const [isMobile, setIsMobile] = useState(window.innerWidth < 800);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 800);
    window.addEventListener("resize", h); return () => window.removeEventListener("resize", h);
  }, []);

  // ── listen to webhooks collection ──
  useEffect(() => {
    if (!merchantId) return;
    dispatch(listenToWebhooks(merchantId, mode));
    return () => { dispatch(cleanupWebhookListener()); };
  }, [merchantId, mode]);

  // ── wizard state ──
  const [showWizard, setShowWizard] = useState(false);
  const [step,       setStep]       = useState<Step>(1);
  const [selected,   setSelected]   = useState<Set<string>>(new Set(DEFAULT_EVENTS));
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set(["Payment Intent", "Checkout"]));
  const [destType,   setDestType]   = useState<"webhook" | "cosmoola">("webhook");
  const [whName,     setWhName]     = useState("");
  const [whUrl,      setWhUrl]      = useState("");
  const [whDesc,     setWhDesc]     = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [copied,        setCopied]        = useState(false);
  const [rotateModal, setRotateModal] = useState<{ id: string; secret: string } | null>(null);
  const [rotateBusy,  setRotateBusy]  = useState<string | null>(null);

  const resetWizard = () => {
    setShowWizard(false); setStep(1);
    setSelected(new Set(DEFAULT_EVENTS));
    setOpenGroups(new Set(["Payment Intent", "Checkout"]));
    setDestType("webhook"); setWhName(""); setWhUrl(""); setWhDesc("");
    setLocalError(null); setCreatedSecret(null);
  };

  const handleCreate = async () => {
    setLocalError(null);
    if (!whUrl.startsWith("https://")) { setLocalError("URL must start with https://"); return; }
    try {
      const res  = await fetch(`${API}/registerWebhook`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${secretKey}`, "Content-Type": "application/json" },
        body:    JSON.stringify({ url: whUrl, enabled_events: Array.from(selected), description: whDesc || undefined }),
      });
      const data = await res.json();
      if (!res.ok) { setLocalError(data?.error?.message || "Failed to create webhook"); return; }

      await dispatch(upsertWebhook({
        merchantId,
        url:            whUrl,
        enabled_events: Array.from(selected),
        description:    whDesc || undefined,
        mode,
        metadata:       { name: whName, backend_id: data.id },
      }));

      setCreatedSecret(data.secret);
      setStep(4);
    } catch (e: any) {
      setLocalError(e.message || "Network error");
    }
  };

  const handleRotate = async (ep: WebhookEndpoint) => {
    setRotateBusy(ep.id);
    try {
      const res  = await fetch(`${API}/generateWebhookSecret`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${secretKey}`, "Content-Type": "application/json" },
        body:    JSON.stringify({ webhook_id: ep.metadata?.backend_id || ep.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error?.message || "Failed to rotate secret");

      await dispatch(upsertWebhook({
        id: ep.id, merchantId, url: ep.url, enabled_events: ep.enabled_events, mode,
        has_secret: true, secret_rotated_at: data.rotated_at ?? Math.floor(Date.now() / 1000),
      } as any));

      setRotateModal({ id: ep.id, secret: data.secret });
    } catch (e: any) {
      setLocalError(e.message || "Rotation failed");
    } finally {
      setRotateBusy(null);
    }
  };

  const handleToggleStatus = async (ep: WebhookEndpoint) => {
    await dispatch(upsertWebhook({
      id: ep.id, merchantId, url: ep.url, enabled_events: ep.enabled_events, mode,
      status: ep.status === "enabled" ? "disabled" : "enabled",
    } as any));
  };

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const toggleEvent     = (id: string, on: boolean) =>
    setSelected(p => { const n = new Set(p); on ? n.add(id) : n.delete(id); return n; });
  const toggleGroupAll  = (group: string, on: boolean) => {
    const g = EVENT_GROUPS.find(g => g.group === group)!;
    setSelected(p => { const n = new Set(p); g.events.forEach(e => on ? n.add(e.id) : n.delete(e.id)); return n; });
  };
  const toggleGroupOpen = (group: string) =>
    setOpenGroups(p => { const n = new Set(p); n.has(group) ? n.delete(group) : n.add(group); return n; });

  const step3Valid = whName.trim().length > 0 && whUrl.startsWith("https://");

  return (
    <div style={{ minHeight: "100vh", background: IVORY, ...sans }}>

      {/* HEADER */}
      <div style={{ background: PURE_B, padding: isMobile ? "28px 20px" : "36px 56px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <div style={{ ...serif, fontSize: isMobile ? 22 : 28, fontWeight: 300, letterSpacing: "0.28em", color: PURE_W }}>COSMOOLA</div>
          <div style={{ fontSize: 9, letterSpacing: "0.35em", color: WARMGRAY, marginTop: 6, textTransform: "uppercase" as const, fontWeight: 500 }}>Webhook Endpoints · {mode}</div>
        </div>
        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} onClick={() => setShowWizard(true)}
          style={{ ...sans, padding: isMobile ? "10px 16px" : "12px 28px", border: `1.5px solid ${PURE_W}`, background: "transparent", color: PURE_W, fontWeight: 600, fontSize: 11, cursor: "pointer", letterSpacing: "0.14em", textTransform: "uppercase" as const }}>
          + Add Endpoint
        </motion.button>
      </div>

      {/* CONTENT */}
      <div style={{ maxWidth: 900, margin: "0 auto", padding: isMobile ? "28px 16px" : "48px 40px" }}>

        {(storeError || localError) && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            style={{ marginBottom: 20, padding: "12px 16px", background: `${ROUGE}08`, border: `1px solid ${ROUGE}22`, fontSize: 13, color: ROUGE, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>{storeError || localError}</span>
            <button onClick={() => { dispatch(clearError()); setLocalError(null); }}
              style={{ background: "none", border: "none", cursor: "pointer", color: ROUGE, fontWeight: 600, fontSize: 16 }}>✕</button>
          </motion.div>
        )}

        {loading && <div style={{ textAlign: "center", padding: 60, color: WARMGRAY, fontSize: 14 }}>Loading webhooks…</div>}

        {!loading && endpoints.length === 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            style={{ textAlign: "center", padding: isMobile ? "60px 20px" : "100px 40px" }}>
            <div style={{ fontSize: 40, marginBottom: 16, color: STONE }}>⬡</div>
            <h2 style={{ ...serif, fontSize: 28, fontWeight: 300, color: NIGHT, marginBottom: 12, fontStyle: "italic" }}>No endpoints yet</h2>
            <p style={{ ...sans, color: DUSK, fontSize: 13, lineHeight: 1.7, maxWidth: 380, margin: "0 auto 28px" }}>
              Add a webhook endpoint to receive real-time payment events.
            </p>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} onClick={() => setShowWizard(true)}
              style={{ ...sans, padding: "14px 40px", border: `2px solid ${PURE_B}`, background: PURE_B, color: PURE_W, fontWeight: 600, fontSize: 11, cursor: "pointer", letterSpacing: "0.18em", textTransform: "uppercase" as const }}>
              Add Endpoint
            </motion.button>
          </motion.div>
        )}

        {!loading && endpoints.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {endpoints.map(ep => (
              <EndpointCard
                key={ep.id} ep={ep} isMobile={isMobile}
                rotating={rotateBusy === ep.id}
                onRotate={() => handleRotate(ep)}
                onToggleStatus={() => handleToggleStatus(ep)}
              />
            ))}
          </div>
        )}
      </div>

      {/* WIZARD MODAL */}
      <AnimatePresence>
        {showWizard && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={resetWizard}
            style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <motion.div initial={{ opacity: 0, scale: 0.94, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
              onClick={e => e.stopPropagation()}
              style={{ background: PURE_W, width: "100%", maxWidth: 620, maxHeight: "92vh", overflowY: "auto", padding: isMobile ? 24 : 40 }}>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
                <span style={{ ...serif, fontSize: 22, fontWeight: 400, color: NIGHT }}>
                  {step === 4 ? "Endpoint created" : "Create event destination"}
                </span>
                <button onClick={resetWizard}
                  style={{ background: "transparent", border: `1px solid ${STONE}`, width: 34, height: 34, cursor: "pointer", fontSize: 16, color: DUSK }}>✕</button>
              </div>

              {step < 4 && <StepBar step={step} />}

              {/* STEP 1 */}
              {step === 1 && (
                <div>
                  <p style={{ ...sans, fontSize: 13, color: DUSK, marginBottom: 16 }}>Select which events to send. Saved only when you create the destination.</p>
                  {EVENT_GROUPS.map(group => {
                    const isOpen   = openGroups.has(group.group);
                    const groupSel = group.events.filter(e => selected.has(e.id)).length;
                    const allSel   = groupSel === group.events.length;
                    return (
                      <div key={group.group} style={{ borderBottom: `1px solid ${CREAM}` }}>
                        <div onClick={() => toggleGroupOpen(group.group)}
                          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", cursor: "pointer" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 500, color: NIGHT }}>
                            <span style={{ fontSize: 10, color: DUSK, display: "inline-block", transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}>▶</span>
                            {group.group}
                            {groupSel > 0 && <span style={{ background: PURPLE_L, color: PURPLE, fontSize: 10, padding: "1px 7px", borderRadius: 4, fontWeight: 600 }}>{groupSel}</span>}
                          </div>
                          <span style={{ fontSize: 11, color: WARMGRAY }}>{group.events.length} events</span>
                        </div>
                        {isOpen && (
                          <div style={{ paddingLeft: 20, paddingBottom: 8 }}>
                            <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${CREAM}`, cursor: "pointer" }}>
                              <input type="checkbox" checked={allSel} onChange={e => toggleGroupAll(group.group, e.target.checked)} style={{ accentColor: PURPLE }} />
                              <span style={{ fontSize: 12, fontWeight: 500, color: WARMGRAY }}>Select all {group.group} events</span>
                            </label>
                            {group.events.map(ev => (
                              <label key={ev.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: `1px solid ${CREAM}`, cursor: "pointer" }}>
                                <input type="checkbox" checked={selected.has(ev.id)} onChange={e => toggleEvent(ev.id, e.target.checked)}
                                  style={{ accentColor: PURPLE, marginTop: 2, flexShrink: 0 }} />
                                <div>
                                  <div style={{ ...mono, fontSize: 12, fontWeight: 600, color: NIGHT }}>{ev.id}</div>
                                  <div style={{ fontSize: 11, color: WARMGRAY, marginTop: 2 }}>{ev.desc}</div>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${CREAM}` }}>
                    <span style={{ fontSize: 13, color: DUSK }}>{selected.size} events selected</span>
                    <Btn disabled={selected.size === 0} onClick={() => setStep(2)}>Continue →</Btn>
                  </div>
                </div>
              )}

              {/* STEP 2 */}
              {step === 2 && (
                <div>
                  <p style={{ fontSize: 13, color: DUSK, marginBottom: 20 }}>Choose where you want to send events.</p>
                  {[
                    { id: "webhook"  as const, icon: "⇄", label: "Webhook endpoint",       sub: "Send events to a hosted HTTPS endpoint" },
                    { id: "cosmoola" as const, icon: "⚡", label: "Cosmoola event stream", sub: "Stream to your Cosmoola dashboard in real-time" },
                  ].map(d => (
                    <div key={d.id} onClick={() => setDestType(d.id)}
                      style={{ border: `1.5px solid ${destType === d.id ? PURPLE : STONE}`, background: destType === d.id ? PURPLE_L : "transparent", padding: "14px 16px", marginBottom: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, transition: "all 0.15s" }}>
                      <div style={{ width: 36, height: 36, background: destType === d.id ? `${PURPLE}18` : CREAM, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{d.icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: NIGHT }}>{d.label}</div>
                        <div style={{ fontSize: 11, color: DUSK, marginTop: 2 }}>{d.sub}</div>
                      </div>
                      <div style={{ color: destType === d.id ? PURPLE : STONE, fontSize: 18 }}>{destType === d.id ? "●" : "○"}</div>
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${CREAM}` }}>
                    <GhostBtn onClick={() => setStep(1)}>← Back</GhostBtn>
                    <Btn onClick={() => setStep(3)}>Continue →</Btn>
                  </div>
                </div>
              )}

              {/* STEP 3 */}
              {step === 3 && (
                <div>
                  <div style={{ background: "#F8F6F3", padding: "10px 14px", marginBottom: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 12, color: DUSK }}>
                    <span>Events from: <strong style={{ color: NIGHT }}>Your account</strong></span>
                    <span>Listening to: <strong style={{ color: NIGHT }}>{selected.size} events</strong></span>
                  </div>
                  <FieldGroup label="Destination name">
                    <input value={whName} onChange={e => setWhName(e.target.value)} placeholder="e.g. production-checkout-webhook"
                      style={{ width: "100%", padding: "12px 14px", border: `1px solid ${STONE}`, fontSize: 13, color: NIGHT, outline: "none", fontFamily: "'Libre Franklin',sans-serif" }} />
                  </FieldGroup>
                  <FieldGroup label="Endpoint URL">
                    <input value={whUrl} onChange={e => setWhUrl(e.target.value)} placeholder="https://yoursite.com/webhook"
                      style={{ width: "100%", padding: "12px 14px", border: `1px solid ${whUrl && !whUrl.startsWith("https://") ? ROUGE : STONE}`, fontSize: 13, color: NIGHT, outline: "none", ...mono }} />
                    {whUrl && !whUrl.startsWith("https://") && (
                      <span style={{ fontSize: 11, color: ROUGE, marginTop: 4, display: "block" }}>URL must start with https://</span>
                    )}
                  </FieldGroup>
                  <FieldGroup label="Description (optional)">
                    <textarea value={whDesc} onChange={e => setWhDesc(e.target.value)} rows={3} placeholder="An optional description..."
                      style={{ width: "100%", padding: "12px 14px", border: `1px solid ${STONE}`, fontSize: 13, color: NIGHT, outline: "none", resize: "vertical", fontFamily: "'Libre Franklin',sans-serif" }} />
                  </FieldGroup>
                  {localError && (
                    <div style={{ padding: "10px 14px", background: `${ROUGE}08`, border: `1px solid ${ROUGE}22`, fontSize: 12, color: ROUGE, marginBottom: 16 }}>{localError}</div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${CREAM}` }}>
                    <GhostBtn onClick={() => setStep(2)}>← Back</GhostBtn>
                    <Btn disabled={!step3Valid || upserting} onClick={handleCreate}>
                      {upserting ? "Creating…" : "Create destination"}
                    </Btn>
                  </div>
                </div>
              )}

              {/* STEP 4 */}
              {step === 4 && createdSecret && (
                <div>
                  <div style={{ textAlign: "center", padding: "8px 0 24px" }}>
                    <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#E8F5E9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, margin: "0 auto 16px" }}>✓</div>
                    <p style={{ fontSize: 13, color: DUSK }}>Endpoint registered. Save your signing secret now — it will not be shown again.</p>
                  </div>
                  <div style={{ background: "#FFF8E7", border: `1px solid ${AMBER}44`, padding: "12px 16px", marginBottom: 20, fontSize: 12, color: "#7A6520", lineHeight: 1.7 }}>
                    <strong>⚠️ Save this secret immediately.</strong> Store it in a secure password manager. You cannot retrieve it after closing.
                  </div>
                  <FieldGroup label="Signing secret">
                    <div style={{ display: "flex", gap: 8 }}>
                      <div style={{ flex: 1, background: "#F8F6F3", border: `1px solid ${CREAM}`, padding: "11px 14px", ...mono, fontSize: 12, color: NIGHT, wordBreak: "break-all" }}>
                        {createdSecret}
                      </div>
                      <button onClick={() => copyText(createdSecret)}
                        style={{ ...sans, padding: "0 16px", border: `1px solid ${copied ? SAGE : STONE}`, background: copied ? `${SAGE}0A` : "transparent", color: copied ? SAGE : DUSK, fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
                        {copied ? "✓ Copied" : "Copy"}
                      </button>
                    </div>
                  </FieldGroup>
                  <div style={{ marginTop: 8, fontSize: 12, color: WARMGRAY }}>
                    URL: <span style={{ ...mono, color: NIGHT }}>{whUrl}</span><br />
                    Events: <strong style={{ color: NIGHT }}>{selected.size} selected</strong>
                  </div>
                  <button onClick={resetWizard}
                    style={{ ...sans, width: "100%", marginTop: 24, padding: "15px 0", border: `2px solid ${PURE_B}`, background: PURE_B, color: PURE_W, fontWeight: 600, fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase" as const, cursor: "pointer" }}>
                    I've Saved My Secret
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ROTATE SECRET MODAL */}
      <AnimatePresence>
        {rotateModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setRotateModal(null)}
            style={{ position: "fixed", inset: 0, zIndex: 1001, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <motion.div initial={{ opacity: 0, scale: 0.94, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0 }}
              onClick={e => e.stopPropagation()}
              style={{ background: PURE_W, width: "100%", maxWidth: 520, padding: isMobile ? 24 : 36 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <span style={{ ...serif, fontSize: 20, fontWeight: 400, color: NIGHT }}>New signing secret</span>
                <button onClick={() => setRotateModal(null)}
                  style={{ background: "transparent", border: `1px solid ${STONE}`, width: 32, height: 32, cursor: "pointer", color: DUSK }}>✕</button>
              </div>
              <div style={{ background: "#FFF8E7", border: `1px solid ${AMBER}44`, padding: "12px 14px", marginBottom: 20, fontSize: 12, color: "#7A6520", lineHeight: 1.7 }}>
                <strong>⚠️ Save this secret immediately.</strong> Previous signatures using the old secret will stop working once you update your endpoint.
              </div>
              <FieldGroup label="New signing secret">
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1, background: "#F8F6F3", border: `1px solid ${CREAM}`, padding: "11px 14px", ...mono, fontSize: 12, color: NIGHT, wordBreak: "break-all" }}>
                    {rotateModal.secret}
                  </div>
                  <button onClick={() => copyText(rotateModal.secret)}
                    style={{ ...sans, padding: "0 14px", border: `1px solid ${copied ? SAGE : STONE}`, background: copied ? `${SAGE}0A` : "transparent", color: copied ? SAGE : DUSK, fontSize: 11, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>
                    {copied ? "✓ Copied" : "Copy"}
                  </button>
                </div>
              </FieldGroup>
              <button onClick={() => setRotateModal(null)}
                style={{ ...sans, width: "100%", marginTop: 20, padding: "14px 0", border: `2px solid ${PURE_B}`, background: PURE_B, color: PURE_W, fontWeight: 600, fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase" as const, cursor: "pointer" }}>
                I've Saved My Secret
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ══════════════════════════════════════
   ENDPOINT CARD
   ══════════════════════════════════════ */
function EndpointCard({ ep, isMobile, rotating, onRotate, onToggleStatus }: {
  ep: WebhookEndpoint; isMobile: boolean; rotating: boolean;
  onRotate: () => void; onToggleStatus: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const statusColor = ep.status === "enabled" ? SAGE : WARMGRAY;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      style={{ background: PURE_W, border: `1px solid ${CREAM}`, padding: isMobile ? 20 : 28 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Courier New',monospace", fontSize: 13, color: NIGHT, wordBreak: "break-all" }}>{ep.url}</div>
          <div style={{ fontSize: 11, color: WARMGRAY, marginTop: 4 }}>
            {ep.id} · {ep.enabled_events.length} events · {ep.mode}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.16em", textTransform: "uppercase" as const, color: statusColor, border: `1px solid ${statusColor}33`, padding: "4px 10px", background: `${statusColor}08` }}>
            {ep.status}
          </span>
          <button onClick={() => setExpanded(v => !v)}
            style={{ fontFamily: "'Libre Franklin',sans-serif", padding: "6px 12px", border: `1px solid ${STONE}`, background: "transparent", color: DUSK, fontSize: 11, cursor: "pointer" }}>
            {expanded ? "Hide" : "Manage"}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            style={{ overflow: "hidden" }}>
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${CREAM}` }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.16em", color: WARMGRAY, textTransform: "uppercase" as const, marginBottom: 8 }}>Listening to</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {ep.enabled_events.map(ev => (
                    <span key={ev} style={{ fontFamily: "'Courier New',monospace", fontSize: 10, background: PURPLE_L, color: PURPLE, padding: "2px 8px", borderRadius: 3 }}>{ev}</span>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderTop: `1px solid ${CREAM}` }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: NIGHT }}>Signing secret</div>
                  <div style={{ fontSize: 11, color: WARMGRAY, marginTop: 2 }}>
                    {ep.has_secret
                      ? `Last rotated: ${ep.secret_rotated_at ? new Date(ep.secret_rotated_at * 1000).toLocaleDateString() : "unknown"}`
                      : "No secret generated yet"}
                  </div>
                </div>
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} onClick={onRotate} disabled={rotating}
                  style={{ fontFamily: "'Libre Franklin',sans-serif", padding: "8px 16px", border: `1.5px solid ${NIGHT}`, background: rotating ? "#F8F6F3" : NIGHT, color: rotating ? WARMGRAY : PURE_W, fontSize: 11, fontWeight: 600, cursor: rotating ? "wait" : "pointer", letterSpacing: "0.1em", textTransform: "uppercase" as const }}>
                  {rotating ? "Generating…" : ep.has_secret ? "Rotate Secret" : "Generate Secret"}
                </motion.button>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 12, borderTop: `1px solid ${CREAM}` }}>
                <button onClick={onToggleStatus}
                  style={{ fontFamily: "'Libre Franklin',sans-serif", padding: "7px 16px", border: `1px solid ${ep.status === "enabled" ? ROUGE : SAGE}`, background: "transparent", color: ep.status === "enabled" ? ROUGE : SAGE, fontSize: 11, fontWeight: 600, cursor: "pointer", letterSpacing: "0.08em" }}>
                  {ep.status === "enabled" ? "Disable endpoint" : "Enable endpoint"}
                </button>
              </div>
              {ep.description && (
                <div style={{ fontSize: 12, color: DUSK, paddingTop: 12, borderTop: `1px solid ${CREAM}` }}>{ep.description}</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function StepBar({ step }: { step: Step }) {
  const steps = ["Select events", "Destination type", "Configure"];
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: 28 }}>
      {steps.map((label, i) => {
        const n = i + 1; const done = step > n; const active = step === n;
        return (
          <React.Fragment key={n}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", border: `1.5px solid ${done ? SAGE : active ? PURPLE : STONE}`, background: done ? SAGE : active ? PURPLE_L : "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: done ? PURE_W : active ? PURPLE : WARMGRAY, flexShrink: 0 }}>
                {done ? "✓" : n}
              </div>
              <span style={{ fontSize: 11, fontWeight: active ? 600 : 400, color: active ? NIGHT : WARMGRAY, whiteSpace: "nowrap" as const }}>{label}</span>
            </div>
            {i < steps.length - 1 && <div style={{ flex: 1, height: 1, background: step > n ? SAGE : CREAM, margin: "0 10px" }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.16em", color: WARMGRAY, textTransform: "uppercase" as const, display: "block", marginBottom: 6, fontFamily: "'Libre Franklin',sans-serif" }}>{label}</label>
      {children}
    </div>
  );
}

function Btn({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <motion.button whileHover={!disabled ? { scale: 1.02 } : undefined} whileTap={!disabled ? { scale: 0.97 } : undefined}
      onClick={onClick} disabled={disabled}
      style={{ fontFamily: "'Libre Franklin',sans-serif", padding: "12px 28px", border: `2px solid ${disabled ? STONE : NIGHT}`, background: disabled ? "#F8F6F3" : NIGHT, color: disabled ? WARMGRAY : PURE_W, fontWeight: 600, fontSize: 11, cursor: disabled ? "not-allowed" : "pointer", letterSpacing: "0.14em", textTransform: "uppercase" as const }}>
      {children}
    </motion.button>
  );
}

function GhostBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      style={{ fontFamily: "'Libre Franklin',sans-serif", padding: "12px 20px", border: `1.5px solid ${STONE}`, background: "transparent", color: DUSK, fontWeight: 600, fontSize: 11, cursor: "pointer", letterSpacing: "0.1em", textTransform: "uppercase" as const }}>
      {children}
    </button>
  );
}